import { useCallback, useEffect, useState } from 'react'
import { IdentificationIcon } from '@heroicons/react/24/outline'
import type { AxiosError } from 'axios'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import SettingsIntegrationSection from '@/components/SettingsIntegrationSection'
import { toast } from '@/store/toastStore'
import {
  getExternalCredentials,
  putExternalCredentials,
  type ExternalCredentialResponse,
} from '@/services/accounting'
import { testHomebaseApiCredentials } from '@/services/tips'

function formatTimestamp(value?: string | null): string | null {
  if (!value) return null
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return value
  }
}

function homebaseTestErrorMessage(err: unknown): string {
  const axiosErr = err as AxiosError<{ message?: string }>
  return axiosErr.response?.data?.message ?? 'Connection failed'
}

export default function HomebaseIntegrationSection() {
  const [loading, setLoading] = useState(true)
  const [apiKey, setApiKey] = useState('')
  const [credentialStatus, setCredentialStatus] = useState<ExternalCredentialResponse | null>(
    null
  )
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [inputKey, setInputKey] = useState(0)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const creds = await getExternalCredentials('homebase_api')
      setCredentialStatus(creds)
      setApiKey('')
      setInputKey((k) => k + 1)
    } catch (err) {
      toast.error('Failed to load Homebase API key')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const dirty = apiKey.trim() !== ''

  async function persistKey(): Promise<boolean> {
    const trimmed = apiKey.trim()
    if (!trimmed) {
      toast.warning('Enter an API key to save')
      return false
    }
    try {
      setSaving(true)
      await putExternalCredentials('homebase_api', { api_key: trimmed })
      const creds = await getExternalCredentials('homebase_api')
      setCredentialStatus(creds)
      setApiKey('')
      setInputKey((k) => k + 1)
      toast.success('Homebase API key saved')
      return true
    } catch (err) {
      toast.error('Failed to save Homebase API key')
      console.error(err)
      return false
    } finally {
      setSaving(false)
    }
  }

  async function handleTest() {
    try {
      if (dirty) {
        const saved = await persistKey()
        if (!saved) return
      }
      setTesting(true)
      await testHomebaseApiCredentials()
      toast.success('Connection OK')
      const creds = await getExternalCredentials('homebase_api')
      setCredentialStatus(creds)
    } catch (err) {
      toast.error(homebaseTestErrorMessage(err))
      console.error(err)
    } finally {
      setTesting(false)
    }
  }

  const configured = credentialStatus?.configured ?? false
  const updatedAt = formatTimestamp(credentialStatus?.updated_at)
  const verifiedAt = formatTimestamp(credentialStatus?.last_verified_at)

  const icon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100/90 ring-1 ring-emerald-200/50">
      <IdentificationIcon className="h-5 w-5 text-emerald-700" />
    </div>
  )

  return (
    <SettingsIntegrationSection
      title="Homebase integration"
      icon={icon}
      detail={
        <>
          <p>
            Public API key for api.joinhomebase.com, used by Tip Calculator and Labor Analytics. This
            is not the Homebase browser session used on the Allocate tab.
          </p>
          <p>
            After you save, the field clears on purpose — secrets are not sent back to the browser.
            Leave it blank to keep the stored value. Other users on this Bookkeeper database share
            this key.
          </p>
        </>
      }
    >
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span className={configured ? 'font-medium text-emerald-700' : 'font-medium text-amber-700'}>
              {configured ? 'Configured' : 'Not configured'}
            </span>
            {updatedAt ? <span>Last updated {updatedAt}</span> : null}
            {verifiedAt ? <span>Last verified {verifiedAt}</span> : null}
            {dirty ? <span className="text-amber-600">Unsaved</span> : null}
          </div>
          <div className="max-w-md">
            <label htmlFor="homebase-api-key" className="mb-1 block text-xs font-medium text-gray-700">
              API key
            </label>
            <input
              key={inputKey}
              id="homebase-api-key"
              type="password"
              className="input w-full text-sm"
              value={apiKey}
              placeholder="••••••••"
              autoComplete="new-password"
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" disabled={testing || saving} onClick={() => void handleTest()}>
              {testing ? 'Testing…' : 'Test'}
            </Button>
            <Button gradient onClick={() => void persistKey()} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </SettingsIntegrationSection>
  )
}
